import { Alert, Box, CssReset, Icons, NavBar, SolvTheme, Stack } from '@solvhealth/jigsaw-v1';
import moment from 'moment';
import React, { ComponentProps, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { PREMIUM_VISIT_NAME } from '~/components/Membership/constants';
import SolvPlusLogo from '~/components/Membership/SolvPlusLogo';
import { ASAP_VALUE } from '~/constants';
import { isTelemedBooking } from '~/core/util/booking';
import { getScheduledAppointmentDateWithTimeZone } from '~/core/util/date';
import { isTelemedLocation } from '~/core/util/location';
import { useSelectActiveLocation } from '~/hooks/redux/location';
import { useSelectNewBooking } from '~/hooks/redux/newBooking';

type Props = {
  goBack?: () => void;
  space?: ComponentProps<typeof Stack>['space'];
  hideBackButton?: boolean;
  suppressTimeSlot?: boolean;
  showSolvPlusLogo?: boolean;
};

/**
 * Layout for the membership booking flow. Adds a simple topbar with the Solv logo and a back button.
 */
const Layout = ({
  children,
  goBack,
  hideBackButton,
  showSolvPlusLogo,
  space = 0,
  suppressTimeSlot = false,
}: PropsWithChildren<Props>) => {
  const booking = useSelectNewBooking();

  const { t } = useTranslation(['membership']);

  return (
    <Stack alignItems="stretch" flex="1" pb={8} space={space}>
      <Stack>
        <NavBar hideBackButton={hideBackButton} onGoBack={goBack}>
          {booking.booking.isPremiumInPerson || showSolvPlusLogo ? (
            <SolvPlusLogo />
          ) : (
            t('membership:titles.bookingFlow', { SOLV_NOW_NAME: PREMIUM_VISIT_NAME })
          )}
        </NavBar>
        <Box zIndex={-10}>
          <Alert
            icon={<Icons.Clock />}
            maxContentWidth={`calc(512px -  2 * ${SolvTheme.space[3]})`}
            show={!suppressTimeSlot && !!booking.booking.appointmentTime}
            tone="info"
            variant="banner"
          >
            {booking.booking.appointmentType !== ASAP_VALUE &&
              getScheduledAppointmentDateWithTimeZone(booking.booking)}
            {booking.booking.appointmentType === ASAP_VALUE &&
              moment(new Date()).format('[Today], MMM DD — [ASAP (within 15 min)]')}
          </Alert>
        </Box>
      </Stack>

      <Box alignSelf="center" flex="1" justifySelf="stretch" maxWidth={512} mx="auto" width="100%">
        {children}
      </Box>
      <CssReset />
    </Stack>
  );
};

export default Layout;
